<template>
  <div class="page-card">
    <div class="page-header">
      <h2 class="page-title">通告详情</h2>
      <div class="header-actions">
        <el-button v-if="canOffShelf" type="warning" :loading="submitting" @click="handleOffShelf">下架</el-button>
        <el-button v-if="canOnShelf" type="success" plain :loading="submitting" @click="handleOnShelf">上架</el-button>
        <el-button v-if="canBan" type="danger" plain :loading="submitting" @click="handleBan">封禁</el-button>
        <el-button v-if="canUnban" type="primary" plain :loading="submitting" @click="handleUnban">解封</el-button>
        <el-button @click="goBack">返回列表</el-button>
      </div>
    </div>

    <el-descriptions :column="2" border>
      <el-descriptions-item label="通告ID">{{ detail.id || $route.params.id || '-' }}</el-descriptions-item>
      <el-descriptions-item label="标题">{{ getBaseInfo('title') }}</el-descriptions-item>
      <el-descriptions-item label="类型">{{ getBaseInfo('noticeType') || '-' }}</el-descriptions-item>
      <el-descriptions-item label="招募人数">{{ displayValue(getBaseInfo('recruitCount')) }}</el-descriptions-item>
      <el-descriptions-item label="薪资说明">{{ getBaseInfo('salaryDesc') || '-' }}</el-descriptions-item>
      <el-descriptions-item label="区域">{{ displayRegion }}</el-descriptions-item>
      <el-descriptions-item label="详细地址">{{ getBaseInfo('locationDetail') || '-' }}</el-descriptions-item>
      <el-descriptions-item label="状态">{{ statusText(getBaseInfo('status')) }}</el-descriptions-item>
      <el-descriptions-item label="报名截止">{{ getBaseInfo('signupDeadlineAt') || '-' }}</el-descriptions-item>
    </el-descriptions>

    <el-divider>时间信息</el-divider>
    <div class="info-grid time-grid">
      <div class="info-item">
        <div class="info-label">活动开始日期</div>
        <div class="info-value">{{ datePart(getBaseInfo('activityStartAt')) }}</div>
      </div>
      <div class="info-item">
        <div class="info-label">活动开始时间</div>
        <div class="info-value">{{ timePart(getBaseInfo('activityStartAt')) }}</div>
      </div>
      <div class="info-item">
        <div class="info-label">活动结束日期</div>
        <div class="info-value">{{ datePart(getBaseInfo('activityEndAt')) }}</div>
      </div>
      <div class="info-item">
        <div class="info-label">活动结束时间</div>
        <div class="info-value">{{ timePart(getBaseInfo('activityEndAt')) }}</div>
      </div>
      <div class="info-item">
        <div class="info-label">报名截止日期</div>
        <div class="info-value">{{ datePart(getBaseInfo('signupDeadlineAt')) }}</div>
      </div>
      <div class="info-item">
        <div class="info-label">报名截止时间</div>
        <div class="info-value">{{ timePart(getBaseInfo('signupDeadlineAt')) }}</div>
      </div>
    </div>

    <el-divider>招募条件</el-divider>
    <div class="info-grid recruit-grid">
      <div class="info-item">
        <div class="info-label">年龄下限</div>
        <div class="info-value">{{ displayValue(getBaseInfo('ageMin')) }}</div>
      </div>
      <div class="info-item">
        <div class="info-label">年龄上限</div>
        <div class="info-value">{{ displayValue(getBaseInfo('ageMax')) }}</div>
      </div>
      <div class="info-item span-2">
        <div class="info-label">性别要求</div>
        <div class="info-value">{{ genderText(getBaseInfo('genderRequirement')) }}</div>
      </div>
      <div class="info-item">
        <div class="info-label">身高下限</div>
        <div class="info-value">{{ displayValue(getBaseInfo('heightMin')) }}</div>
      </div>
      <div class="info-item">
        <div class="info-label">身高上限</div>
        <div class="info-value">{{ displayValue(getBaseInfo('heightMax')) }}</div>
      </div>
      <div class="info-item">
        <div class="info-label">体重下限</div>
        <div class="info-value">{{ displayValue(getBaseInfo('weightMin')) }}</div>
      </div>
      <div class="info-item">
        <div class="info-label">体重上限</div>
        <div class="info-value">{{ displayValue(getBaseInfo('weightMax')) }}</div>
      </div>
      <div class="info-item span-2">
        <div class="info-label">技能要求</div>
        <div class="info-value">{{ getBaseInfo('skillRequirement') || '-' }}</div>
      </div>
      <div class="info-item span-2">
        <div class="info-label">风格要求</div>
        <div class="info-value">{{ getBaseInfo('styleRequirement') || '-' }}</div>
      </div>
    </div>

    <el-divider>补充说明</el-divider>
    <div class="textarea-card">
      {{ getBaseInfo('merchantSupportDesc') || '暂无商家补充说明' }}
    </div>
    <div class="textarea-card second-card">
      {{ getBaseInfo('signupNotice') || '暂无报名须知' }}
    </div>

    <el-divider>素材</el-divider>
    <div v-if="visibleMaterials.length" class="material-grid">
      <div v-for="(item, index) in visibleMaterials" :key="item.id || item.mediaUrl || index" class="material-item">
        <div class="material-label">{{ materialTypeText(item.mediaType) }}</div>
        <el-image
          :src="item.mediaUrl"
          :preview-src-list="previewImages"
          fit="cover"
          class="material-image"
        >
          <div slot="error" class="material-error">图片加载失败</div>
        </el-image>
      </div>
    </div>
    <el-empty v-else description="暂无素材" :image-size="88" />

    <el-divider>审核记录</el-divider>
    <el-table :data="detail.auditHistory || []" border>
      <el-table-column label="状态">
        <template slot-scope="{ row }">
          {{ auditStatusText(row.status) }}
        </template>
      </el-table-column>
      <el-table-column prop="reason" label="原因" />
      <el-table-column prop="operatorName" label="处理人" />
      <el-table-column prop="operatedAt" label="处理时间" />
    </el-table>

    <div class="action-bar">
      <el-button v-if="canApproveOrReject" type="success" :loading="submitting" @click="approve">审核通过</el-button>
      <el-button v-if="canApproveOrReject" type="danger" :loading="submitting" @click="openRejectDialog">驳回</el-button>
    </div>

    <el-dialog title="驳回原因" :visible.sync="dialogVisible" width="480px" @closed="resetRejectDialog">
      <el-input v-model="reason" type="textarea" :rows="4" placeholder="请输入驳回原因" />
      <span slot="footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="submitting" @click="reject">确认</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { MessageBox } from 'element-ui'
import {
  approveCastingNotice,
  banCastingNotice,
  getCastingNoticeAuditDetail,
  offShelfCastingNotice,
  onShelfCastingNotice,
  rejectCastingNotice,
  unbanCastingNotice
} from '../../api/audit'
import { hasPermission } from '../../utils/auth'
import { navigateWithFallback } from '../../utils/admin-navigation'
import { normalizeMediaItem } from '../../utils/format'

const DEFAULT_STATUS = 1
const DEFAULT_PAGE = 1

export default {
  name: 'CastingNoticeDetailPage',
  data() {
    return {
      detail: {},
      dialogVisible: false,
      reason: '',
      submitting: false
    }
  },
  computed: {
    visibleMaterials() {
      return (this.detail.materials || []).map(normalizeMediaItem).filter(Boolean)
    },
    previewImages() {
      return this.visibleMaterials.map(item => item.mediaUrl).filter(Boolean)
    },
    currentStatus() {
      return Number(this.getBaseInfo('status')) || 0
    },
    canApproveOrReject() {
      return this.currentStatus === 2
    },
    canOffShelf() {
      return this.currentStatus === 5 && hasPermission('casting_notice_audit:off_shelf')
    },
    canOnShelf() {
      return this.currentStatus === 6
    },
    canBan() {
      return (this.currentStatus === 5 || this.currentStatus === 6) && hasPermission('casting_notice_audit:ban')
    },
    canUnban() {
      return this.currentStatus === 9 && hasPermission('casting_notice_audit:unban')
    },
    displayRegion() {
      const city = this.getBaseInfo('workCity')
      const area = this.getBaseInfo('workArea')
      if (city && area) return `${city} / ${area}`
      return city || area || '-'
    }
  },
  created() {
    this.fetchData()
  },
  methods: {
    getBaseInfo(key) {
      return this.detail.baseInfo ? this.detail.baseInfo[key] : ''
    },
    displayValue(value) {
      return value === 0 || value ? String(value) : '-'
    },
    datePart(value) {
      return value ? String(value).slice(0, 10) : '-'
    },
    timePart(value) {
      return value ? String(value).slice(11, 16) : '-'
    },
    statusText(status) {
      if (status === 2) return '待审核'
      if (status === 4) return '已驳回'
      if (status === 5) return '招募中'
      if (status === 6) return '已下架'
      if (status === 9) return '已封禁'
      return status == null ? '-' : String(status)
    },
    auditStatusText(status) {
      if (status === 1) return '待审核'
      if (status === 2) return '审核通过'
      if (status === 3) return '已驳回'
      return status == null ? '-' : String(status)
    },
    materialTypeText(type) {
      if (type === 1 || type === '1') return '样片'
      if (type === 2 || type === '2') return '海报'
      if (type === 3 || type === '3') return '脚本'
      if (type === 4 || type === '4') return '参考图'
      if (type === 'image') return '图片'
      return type || '素材'
    },
    genderText(value) {
      if (value === 0 || value === '0') return '不限'
      if (value === 1 || value === '1') return '仅男'
      if (value === 2 || value === '2') return '仅女'
      return '-'
    },
    goBack() {
      const query = this.$route.query || {}
      if (query.from === 'hall') {
        navigateWithFallback(this, {
          path: '/casting-hall',
          query: {
            status: query.fromStatus || '5'
          }
        })
        return
      }
      if (query.from === 'dashboard') {
        navigateWithFallback(this, {
          path: '/dashboard',
          query: {
            auditType: query.fromType || 'casting-notice',
            auditPage: query.fromPage || String(DEFAULT_PAGE)
          }
        })
        return
      }
      navigateWithFallback(this, {
        path: '/audits',
        query: {
          type: query.fromType || 'casting-notice',
          status: query.fromStatus || String(DEFAULT_STATUS),
          page: query.fromPage || String(DEFAULT_PAGE)
        }
      })
    },
    async fetchData() {
      try {
        this.detail = await getCastingNoticeAuditDetail(this.$route.params.id)
      } catch (error) {
        this.$message.error(error.message || '加载通告详情失败')
      }
    },
    async approve() {
      if (this.submitting) return
      try {
        await MessageBox.confirm('确认审核通过该通告吗？', '审核确认', {
          type: 'warning',
          confirmButtonText: '确认通过',
          cancelButtonText: '取消'
        })
      } catch (e) {
        return
      }
      try {
        this.submitting = true
        await approveCastingNotice(this.$route.params.id, { remark: '审核通过' })
        this.$message.success('操作成功')
        this.goBack()
      } catch (error) {
        this.$message.error(error.message || '审核通过失败')
      } finally {
        this.submitting = false
      }
    },
    openRejectDialog() {
      this.dialogVisible = true
    },
    resetRejectDialog() {
      this.reason = ''
    },
    async reject() {
      if (this.submitting) return
      if (!this.reason) {
        this.$message.warning('请输入驳回原因')
        return
      }
      try {
        this.submitting = true
        await rejectCastingNotice(this.$route.params.id, { reason: this.reason, remark: this.reason })
        this.dialogVisible = false
        this.$message.success('操作成功')
        this.goBack()
      } catch (error) {
        this.$message.error(error.message || '驳回失败')
      } finally {
        this.submitting = false
      }
    },
    async handleOffShelf() {
      await this.confirmAndAct('确认下架该通告吗？', offShelfCastingNotice, '下架成功')
    },
    async handleOnShelf() {
      await this.confirmAndAct('确认上架该通告吗？', onShelfCastingNotice, '上架成功')
    },
    async handleBan() {
      await this.confirmAndAct('确认封禁该通告吗？', banCastingNotice, '封禁成功')
    },
    async handleUnban() {
      await this.confirmAndAct('确认解封该通告吗？', unbanCastingNotice, '解封成功')
    },
    async confirmAndAct(message, action, successMessage) {
      if (this.submitting) return
      try {
        await MessageBox.confirm(message, '操作确认', {
          type: 'warning',
          confirmButtonText: '确认',
          cancelButtonText: '取消'
        })
      } catch (e) {
        return
      }
      try {
        this.submitting = true
        await action(this.$route.params.id, {})
        this.$message.success(successMessage)
        this.goBack()
      } catch (error) {
        this.$message.error(error.message || '操作失败')
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>

<style scoped>
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.header-actions {
  display: flex;
  gap: 12px;
}

.info-grid {
  display: grid;
  gap: 12px;
}

.time-grid,
.recruit-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.info-item {
  padding: 14px 16px;
  border: 1px solid #dfe6ee;
  border-radius: 10px;
  background: #f7f9fc;
}

.span-2 {
  grid-column: span 2;
}

.info-label {
  margin-bottom: 8px;
  font-size: 13px;
  color: #6b7280;
}

.info-value {
  font-size: 16px;
  color: #111827;
  word-break: break-word;
}

.textarea-card {
  min-height: 120px;
  padding: 16px;
  border: 1px solid #dfe6ee;
  border-radius: 10px;
  background: #f7f9fc;
  color: #374151;
  white-space: pre-wrap;
  word-break: break-word;
}

.second-card {
  margin-top: 14px;
}

.material-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 16px;
}

.material-item {
  border: 1px solid #ebeef5;
  border-radius: 8px;
  padding: 12px;
  background: #fff;
}

.material-label {
  margin-bottom: 10px;
  font-size: 13px;
  color: #606266;
}

.material-image {
  width: 100%;
  height: 180px;
  border-radius: 6px;
  background: #f5f7fa;
}

.material-error {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #c0c4cc;
  background: #f5f7fa;
}

.action-bar {
  margin-top: 20px;
}
</style>
